/**
 * Kit Builder — Phase 2 client behaviors for `sections/kit-builder.liquid`.
 *
 * Custom element `<jr-kit-builder>` wraps Phase 1 markup. Mirrors live
 * `productBundleForm` + `swatchSelector` + accordion init, with Liquid Ajax Cart
 * for multi-line bundle adds and `window.JR.refreshAjaxCartSections` after success.
 */

window.JR = window.JR || {};
if (typeof window.JR.refreshAjaxCartSections !== 'function') {
  window.JR.refreshAjaxCartSections = function refreshAjaxCartSections() {
    document.querySelectorAll('[data-ajax-cart-section]').forEach((cartEl) => {
      const section = cartEl.closest('[id^="shopify-section-"]');
      if (!section) return;
      const sid = section.id.replace('shopify-section-', '');
      fetch(`${window.location.pathname}?sections=${sid}`)
        .then((r) => r.json())
        .then((data) => {
          if (!data[sid]) return;
          const doc = new DOMParser().parseFromString(data[sid], 'text/html');
          const fresh = doc.querySelector('[data-ajax-cart-section]');
          if (fresh) cartEl.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    });
  };
}

const EL_FORM = '.js-bundleForm';
const EL_PRODUCT = '.js-bundleProduct';
const EL_OPTION = '.js-bundleOption';
const EL_LABEL = '.js-bundleVariantLabel';
const EL_VARIANT = '.js-bundleVariant';
const EL_BUTTON = '.js-bundleFormSubmit';
const EL_BUTTON_COPY = '.js-bundleFormCopy';
const EL_SWATCH = '.js-productFormSwatch';

const ACCORDION_SEL = '.js-accordion';
const ACCORDION_BTN = '[data-controls]';
const RENDER_DELAY = 25;
const ANIMATION_DELAY = 425;

function kbRefreshCartAndOpen() {
  if (typeof window.JR.refreshAjaxCartSections === 'function') {
    window.JR.refreshAjaxCartSections();
  }
  window.dispatchEvent(new CustomEvent('cart-open'));
}

function kbRandomUUID() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function kbParseProductDataset(str) {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch (e1) {
    try {
      return JSON.parse(decodeURIComponent(str));
    } catch (e2) {
      return null;
    }
  }
}

function kbVariantsEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function kbFindVariant(variants, selectedOptions) {
  if (!variants || !selectedOptions || selectedOptions.some((x) => x == null || x === '')) {
    return null;
  }
  return variants.find((v) => kbVariantsEqual(v.options, selectedOptions)) || null;
}

function kbSelectedOptionValues(elProduct, productJson) {
  return Array.from(elProduct.querySelectorAll('input:checked')).map((el) => el.value);
}

function kbUpdateVariantImage(elProduct, selectedVariant) {
  if (!selectedVariant || !selectedVariant.media || !selectedVariant.media.length) return;
  const first = selectedVariant.media[0];
  const src = first && first.src;
  if (!src) return;
  const img = elProduct.querySelector('.bundle__product-image .image img');
  if (img) img.setAttribute('src', src);
}

function kbUpdateVariantLabelling(elOption, swatchName = 'Select Shade', description = '') {
  const elLabel = elOption.querySelector(EL_LABEL);
  if (!elLabel) return;
  const elSwatchName = elLabel.querySelector('.name');
  const elSwatchDescription = elLabel.querySelector('.description');
  if (elSwatchName) {
    elSwatchName.innerHTML = swatchName;
  } else {
    elLabel.textContent = swatchName;
  }
  if (elSwatchDescription) {
    elSwatchDescription.innerHTML = description || '';
  }
}

function kbProductShadesResolved(elProduct) {
  const radios = elProduct.querySelectorAll('input[type="radio"]');
  if (!radios.length) return true;
  return Array.from(radios).some((r) => r.checked);
}

function kbUpdateCartButton(elForm) {
  const elButton = elForm.querySelector(EL_BUTTON);
  const elCopy = elButton && elButton.querySelector(EL_BUTTON_COPY);
  const elVariants = Array.from(elForm.querySelectorAll(EL_VARIANT));
  const elProducts = Array.from(elForm.querySelectorAll(EL_PRODUCT));

  if (!elButton) return;

  const available = elVariants.length
    ? elVariants.every((el) => el.getAttribute('data-available') === 'true')
    : true;
  const hasSelectedAllShades = elProducts.every(kbProductShadesResolved);

  const textSpan = elCopy && elCopy.querySelector('.text');

  if (hasSelectedAllShades) {
    if (textSpan) {
      textSpan.textContent = available ? 'Add to Cart' : 'Sold Out';
    } else if (elCopy) {
      elCopy.textContent = available ? 'Add to Cart' : 'Sold Out';
    }
    elButton.disabled = !available;
    if (available) elButton.classList.remove('button--gray');
    else elButton.classList.add('button--gray');
  } else {
    if (textSpan) textSpan.textContent = 'Select Shades';
    elButton.disabled = true;
    elButton.classList.add('button--gray');
  }
}

function kbBuildCartItems(elVariants, elForm) {
  const bundle = elForm.dataset.bundle;
  const image = elForm.dataset.image;
  const url = elForm.dataset.url;
  const id = elForm.dataset.id;
  let discount = elForm.dataset.discount || '';
  if (document.querySelector('.loop-returns-activated') != null) {
    discount = 'abc';
  }

  const conditions = [];
  const items = [];

  elVariants.forEach((el) => {
    const productId = Number(el.dataset.product);
    const variantId = Number(el.value);
    const title = el.dataset.title || '';

    let existingCondition = conditions.find((cond) => cond.product === productId);
    let existingItem = items.find((item) => String(item.id) === String(el.value));

    const lineProps = { _title: title };

    if (!existingCondition) conditions.push({ product: productId, quantity: 1 });
    else existingCondition.quantity += 1;
    if (!existingItem) items.push({ id: variantId, quantity: 1, properties: lineProps });
    else existingItem.quantity += 1;
  });

  const bundleId = kbRandomUUID();
  const properties = {
    _bundle_id: bundleId,
    _conditions: conditions.map((obj) => `${obj.product}@${obj.quantity}`).join('+'),
    _d: discount,
    _product: bundle,
    _product_id: Number(id),
    _image: image,
    _url: url,
    _message: bundle,
  };

  const freeGift = elForm.getAttribute('data-free-gift-variant');
  if (freeGift) properties._free_gift_variant = freeGift;

  const settings = window.settings || {};
  if (
    settings.enable_test_promotion &&
    settings.test_promotion_property &&
    settings.test_promotion_property_value
  ) {
    properties[settings.test_promotion_property] = settings.test_promotion_property_value;
  }

  items.forEach((item) => {
    item.properties = Object.assign({}, item.properties, properties);
  });

  // The free gift ships in the SAME add call as the bundle lines — one cart
  // write, no post-add correction and no busy drawer while a second request
  // runs. component-cart-free-gift.js only reconciles drift afterwards.
  const giftVariantId = Number(freeGift) || 0;
  if (giftVariantId) {
    const giftProperties = { '_This is a free gift variant': true };
    if (
      settings.enable_test_promotion &&
      settings.test_promotion_property &&
      settings.test_promotion_property_value
    ) {
      giftProperties[settings.test_promotion_property] = settings.test_promotion_property_value;
    }
    items.push({ id: giftVariantId, quantity: 1, properties: giftProperties });
  }

  return items;
}

// NOTE: Klaviyo "Added to Cart" tracking for the kit builder is handled globally
// by assets/component-klaviyo-add-to-cart.js, which fires on every successful
// /cart/add.js (the kit builder adds via window.liquidAjaxCart.add). The previous
// per-kit kbTrackKlaviyoAddToCart() call was removed to avoid double-firing.

function kbPushDataLayerBundle(form, items) {
  if (typeof window.dataLayer === 'undefined' || !window.dataLayer) return;
  const bundleId = items[0] && items[0].properties && items[0].properties._bundle_id;
  window.dataLayer.push({
    event: 'CustomEvent',
    event_category: 'Kit Builder',
    event_action: 'bundle_add_to_cart',
    bundle_id: bundleId,
    variant_ids: items.map((i) => i.id),
    bundle_product_id: form.dataset.id ? Number(form.dataset.id) : undefined,
  });
}

/** Pair With upsells: metafield variant JSON + radios use `upsell-{productId}-option{n}` names (see product-upsell.liquid). */
function kbUpsellSelectedVariantId(card, fallbackId) {
  const dataEl = card.querySelector('.js-upsellProductJson');
  if (!dataEl) return fallbackId;
  let variants;
  try {
    variants = JSON.parse(dataEl.textContent);
  } catch (e) {
    return fallbackId;
  }
  if (!Array.isArray(variants) || !variants.length) return fallbackId;
  const pid = card.getAttribute('data-upsell-product-id');
  if (!pid) return fallbackId;
  const hasChecked = card.querySelector('input[type="radio"]:checked');
  if (!hasChecked) return fallbackId;
  const selected = [];
  for (let i = 1; i <= 3; i += 1) {
    const inp = card.querySelector(`input[name="upsell-${pid}-option${i}"]:checked`);
    selected.push(inp ? inp.value : null);
  }
  const match = variants.find((v) => {
    if (selected[0] != null && selected[0] !== '' && v.option1 !== selected[0]) return false;
    if (selected[1] != null && selected[1] !== '' && v.option2 !== selected[1]) return false;
    if (selected[2] != null && selected[2] !== '' && v.option3 !== selected[2]) return false;
    return true;
  });
  if (match && match.id != null) return Number(match.id);
  return fallbackId;
}

function kbUpsellSyncAddButtonFromRadios(card) {
  const addBtn = card.querySelector('.js-addToBag.new-upsell');
  if (!addBtn) return;
  const fallback = parseInt(addBtn.getAttribute('data-id'), 10);
  const vid = kbUpsellSelectedVariantId(card, fallback);
  if (vid) addBtn.setAttribute('data-id', String(vid));
}

function kbUpsellLineProperties(btn) {
  const properties = {};
  const compliance = btn.getAttribute('data-compliance-product');
  if (compliance) properties._compliance_product_title = compliance;
  const settings = window.settings || {};
  if (
    settings.enable_test_promotion &&
    settings.test_promotion_property &&
    settings.test_promotion_property_value
  ) {
    properties[settings.test_promotion_property] = settings.test_promotion_property_value;
  }
  return properties;
}

class JrKitBuilder extends HTMLElement {
  constructor() {
    super();
    this._onFormChange = this._onFormChange.bind(this);
    this._onFormSubmit = this._onFormSubmit.bind(this);
    this._onSwatchMouseover = this._onSwatchMouseover.bind(this);
    this._onOptionMouseleave = this._onOptionMouseleave.bind(this);
    this._onAccordionClick = this._onAccordionClick.bind(this);
    this._onSwatchSelectorClick = this._onSwatchSelectorClick.bind(this);
    this._onDocClickCloseSwatch = this._onDocClickCloseSwatch.bind(this);
    this._onPairWithClick = this._onPairWithClick.bind(this);
    this._onUpsellRadioChange = this._onUpsellRadioChange.bind(this);
    this._accordionButtons = [];
    this._activeSwatchPanel = null;
    /** @type {HTMLFormElement | null} */
    this._form = null;
  }

  connectedCallback() {
    this._form = this.querySelector(EL_FORM);
    if (this._form) {
      this._form.addEventListener('change', this._onFormChange);
      this._form.addEventListener('submit', this._onFormSubmit);
    }

    this.querySelectorAll(EL_OPTION).forEach((elOption) => {
      elOption.querySelectorAll(EL_SWATCH).forEach((sw) => {
        sw.addEventListener('mouseover', this._onSwatchMouseover);
      });
      elOption.addEventListener('mouseleave', this._onOptionMouseleave);
    });

    this.querySelectorAll('.js-swatchSelector').forEach((btn) => {
      btn.addEventListener('click', this._onSwatchSelectorClick);
    });

    this._bindAccordions();

    if (this._form) kbUpdateCartButton(this._form);

    this.addEventListener('click', this._onPairWithClick);
    this.addEventListener('change', this._onUpsellRadioChange);
    this.querySelectorAll('.product__upsell').forEach((card) => kbUpsellSyncAddButtonFromRadios(card));
  }

  disconnectedCallback() {
    if (this._form) {
      this._form.removeEventListener('change', this._onFormChange);
      this._form.removeEventListener('submit', this._onFormSubmit);
      this._form = null;
    }

    this.querySelectorAll(EL_OPTION).forEach((elOption) => {
      elOption.querySelectorAll(EL_SWATCH).forEach((sw) => {
        sw.removeEventListener('mouseover', this._onSwatchMouseover);
      });
      elOption.removeEventListener('mouseleave', this._onOptionMouseleave);
    });

    this.querySelectorAll('.js-swatchSelector').forEach((btn) => {
      btn.removeEventListener('click', this._onSwatchSelectorClick);
    });

    document.removeEventListener('click', this._onDocClickCloseSwatch);
    this._unbindAccordions();

    this.removeEventListener('click', this._onPairWithClick);
    this.removeEventListener('change', this._onUpsellRadioChange);
  }

  _bindAccordions() {
    this.querySelectorAll(`${ACCORDION_SEL} ${ACCORDION_BTN}`).forEach((buttonEl) => {
      const controlsId = buttonEl.getAttribute('data-controls');
      if (!controlsId) return;
      const panel = document.getElementById(controlsId);
      if (!panel || !this.contains(panel)) return;

      buttonEl._kbPanelEl = panel;
      buttonEl.addEventListener('click', this._onAccordionClick);
      this._accordionButtons.push(buttonEl);

      const isExpanded = panel.getAttribute('aria-hidden') === 'false';

      buttonEl.setAttribute('aria-controls', panel.id);
      buttonEl.setAttribute('aria-expanded', isExpanded.toString());

      panel.setAttribute('style', isExpanded ? 'height: auto' : 'height: 0px');
      panel.setAttribute('aria-hidden', (!isExpanded).toString());
      if (buttonEl.id) panel.setAttribute('aria-labelledby', buttonEl.id);
    });
  }

  _unbindAccordions() {
    this._accordionButtons.forEach((buttonEl) => {
      buttonEl.removeEventListener('click', this._onAccordionClick);
      if (buttonEl._renderTimeout) clearTimeout(buttonEl._renderTimeout);
      if (buttonEl._animationTimeout) clearTimeout(buttonEl._animationTimeout);

      const panelEl = buttonEl._kbPanelEl;
      buttonEl.removeAttribute('aria-controls');
      buttonEl.removeAttribute('aria-expanded');

      if (panelEl) {
        panelEl.removeAttribute('style');
        panelEl.removeAttribute('aria-hidden');
        panelEl.removeAttribute('aria-labelledby');
      }
      buttonEl._kbPanelEl = null;
    });
    this._accordionButtons = [];
  }

  _onPairWithClick(ev) {
    const reveal = ev.target.closest('.product__upsell-reveal');
    if (reveal && this.contains(reveal)) {
      ev.preventDefault();
      const card = reveal.closest('.product__upsell');
      if (!card) return;
      card.querySelectorAll('.product__form-swatches').forEach((ul) => {
        ul.style.display = '';
      });
      const add = card.querySelector('.js-addToBag.new-upsell');
      if (add) {
        add.style.display = 'flex';
        kbUpsellSyncAddButtonFromRadios(card);
      }
      reveal.style.display = 'none';
      return;
    }

    const btn = ev.target.closest('.js-addToBag.new-upsell');
    if (!btn || !this.contains(btn)) return;
    ev.preventDefault();

    if (!window.liquidAjaxCart || typeof window.liquidAjaxCart.add !== 'function') {
      return;
    }

    const card = btn.closest('.product__upsell');
    if (card) kbUpsellSyncAddButtonFromRadios(card);

    const variantId = parseInt(btn.getAttribute('data-id'), 10);
    if (!variantId) return;

    const props = kbUpsellLineProperties(btn);
    const item = { id: variantId, quantity: 1 };
    if (Object.keys(props).length) item.properties = props;

    const labelEl = btn.querySelector('.product__upsell-label');
    const priceEl = btn.querySelector('.product__upsell-price');
    const prevLabel = labelEl ? labelEl.textContent : '';
    const prevPriceDisplay = priceEl ? priceEl.style.display : '';
    if (labelEl) labelEl.textContent = 'Adding...';
    if (priceEl) priceEl.style.display = 'none';
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;

    window.liquidAjaxCart.add(
      { items: [item] },
      {
        info: { initiator: this },
        lastCallback: (requestState) => {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
          if (labelEl) labelEl.textContent = prevLabel;
          if (priceEl) priceEl.style.display = prevPriceDisplay;

          if (requestState.responseData && requestState.responseData.ok) {
            kbRefreshCartAndOpen();
            if (labelEl) labelEl.textContent = btn.getAttribute('data-added-copy') || 'Added';
            if (priceEl) priceEl.style.display = 'none';
            setTimeout(() => {
              if (labelEl) labelEl.textContent = prevLabel;
              if (priceEl) priceEl.style.display = prevPriceDisplay;
            }, 1500);
          }
        },
      }
    );
  }

  _onUpsellRadioChange(ev) {
    const t = ev.target;
    if (!(t instanceof HTMLInputElement) || t.type !== 'radio') return;
    const name = t.getAttribute('name') || '';
    if (!name.startsWith('upsell-')) return;
    const card = t.closest('.product__upsell');
    if (!card || !this.contains(card)) return;
    kbUpsellSyncAddButtonFromRadios(card);
  }

  _onAccordionClick(ev) {
    const buttonEl = ev.currentTarget;
    const panelEl = buttonEl._kbPanelEl;
    if (!panelEl || !panelEl.firstElementChild) return;

    const willExpand = buttonEl.getAttribute('aria-expanded') === 'false';
    const height = panelEl.firstElementChild.scrollHeight;

    clearTimeout(buttonEl._renderTimeout);
    clearTimeout(buttonEl._animationTimeout);

    buttonEl.setAttribute('aria-expanded', willExpand);
    panelEl.setAttribute('style', willExpand ? 'height: 0px' : `height: ${height}px`);

    buttonEl._renderTimeout = setTimeout(() => {
      panelEl.setAttribute('style', willExpand ? `height: ${height}px` : 'height: 0px');
      panelEl.setAttribute('aria-hidden', (!willExpand).toString());
      if (willExpand) {
        const c = panelEl.firstElementChild;
        if (c && c.tagName === 'DIV') c.setAttribute('tabindex', '-1');
        if (c) c.focus();
        if (c && c.tagName === 'DIV') c.removeAttribute('tabindex');
      }
    }, RENDER_DELAY);

    buttonEl._animationTimeout = setTimeout(() => {
      panelEl.setAttribute('style', willExpand ? 'height: auto' : 'height: 0px');
    }, ANIMATION_DELAY);
  }

  _onSwatchSelectorClick(ev) {
    ev.preventDefault();
    const parent = ev.currentTarget.parentElement;
    if (!parent) return;

    if (this._activeSwatchPanel && this._activeSwatchPanel !== parent) {
      this._activeSwatchPanel.classList.remove('is-expanded');
    }

    if (parent.classList.contains('is-expanded')) {
      parent.classList.remove('is-expanded');
      this._activeSwatchPanel = null;
      document.removeEventListener('click', this._onDocClickCloseSwatch);
      return;
    }

    parent.classList.add('is-expanded');
    this._activeSwatchPanel = parent;
    setTimeout(() => {
      document.addEventListener('click', this._onDocClickCloseSwatch);
    }, 0);
  }

  _onDocClickCloseSwatch(ev) {
    if (!this._activeSwatchPanel || this._activeSwatchPanel.contains(ev.target)) return;
    this._activeSwatchPanel.classList.remove('is-expanded');
    this._activeSwatchPanel = null;
    document.removeEventListener('click', this._onDocClickCloseSwatch);
  }

  _onFormChange(ev) {
    const t = ev.target;
    if (!(t instanceof HTMLInputElement) || t.type !== 'radio') return;

    const elForm = ev.currentTarget;
    const elProduct = t.closest(EL_PRODUCT);
    const elOption = t.closest(EL_OPTION);
    const elVariant = elProduct && elProduct.querySelector(EL_VARIANT);
    if (!elProduct || !elOption || !elVariant) return;

    const productJson = kbParseProductDataset(elProduct.dataset.product);
    const selectedValues = kbSelectedOptionValues(elProduct, productJson);
    const variants = productJson && productJson.variants;
    const selectedVariant = kbFindVariant(variants, selectedValues);

    elVariant.value = selectedVariant ? String(selectedVariant.id) : '';
    elVariant.setAttribute('data-available', selectedVariant ? String(selectedVariant.available) : 'false');
    elVariant.setAttribute('data-title', selectedVariant ? selectedVariant.title : '');

    kbUpdateVariantImage(elProduct, selectedVariant);
    kbUpdateCartButton(elForm);
    kbUpdateVariantLabelling(
      elOption,
      t.value,
      selectedVariant && selectedVariant.description ? selectedVariant.description : ''
    );
  }

  _onSwatchMouseover(ev) {
    const sw = ev.currentTarget;
    if (!(sw instanceof Element) || !sw.classList.contains('js-productFormSwatch')) return;
    if (!this.contains(sw)) return;

    const elProduct = sw.closest(EL_PRODUCT);
    const elOption = sw.closest(EL_OPTION);
    if (!elProduct || !elOption) return;

    const { option, value } = sw.dataset;
    const productJson = kbParseProductDataset(elProduct.dataset.product);
    if (!productJson || !productJson.variants) return;

    const selectedValues = kbSelectedOptionValues(elProduct, productJson).slice();
    const optKey = (option || '').split('-')[0];
    const idx =
      optKey === 'option1' ? 0 : optKey === 'option2' ? 1 : optKey === 'option3' ? 2 : -1;
    if (idx >= 0) selectedValues[idx] = value;

    const selectedVariant = kbFindVariant(productJson.variants, selectedValues);
    if (!selectedVariant) return;

    kbUpdateVariantImage(elProduct, selectedVariant);
    kbUpdateVariantLabelling(
      elOption,
      value,
      selectedVariant.description ? selectedVariant.description : ''
    );
  }

  _onOptionMouseleave(ev) {
    const elOption = ev.currentTarget;
    const elProduct = elOption.closest(EL_PRODUCT);
    if (!elProduct) return;

    const productJson = kbParseProductDataset(elProduct.dataset.product);
    if (!productJson || !productJson.variants) return;

    const selectedValues = kbSelectedOptionValues(elProduct, productJson);
    const selectedVariant = kbFindVariant(productJson.variants, selectedValues);

    if (selectedVariant) {
      kbUpdateVariantImage(elProduct, selectedVariant);
      const firstChecked = elProduct.querySelector('input[type="radio"]:checked');
      kbUpdateVariantLabelling(
        elOption,
        firstChecked ? firstChecked.value : 'Select Shade',
        selectedVariant.description ? selectedVariant.description : ''
      );
    } else if (productJson.variants[0]) {
      kbUpdateVariantImage(elProduct, productJson.variants[0]);
      kbUpdateVariantLabelling(elOption);
    }
  }

  _onFormSubmit(ev) {
    ev.preventDefault();
    const form = ev.currentTarget;
    const btn = form.querySelector(EL_BUTTON);
    if (!btn || btn.disabled) return;

    if (!window.liquidAjaxCart || typeof window.liquidAjaxCart.add !== 'function') {
      return;
    }

    const elVariants = form.querySelectorAll(EL_VARIANT);
    const items = kbBuildCartItems(elVariants, form);
    if (!items.length) return;

    btn.classList.add('is-loading');
    btn.disabled = true;

    window.liquidAjaxCart.add(
      { items },
      {
        info: { initiator: this },
        lastCallback: (requestState) => {
          btn.classList.remove('is-loading');
          kbUpdateCartButton(form);
          if (requestState.responseData && requestState.responseData.ok) {
            // Klaviyo "Added to Cart" fires globally via the /cart/add.js fetch
            // hook (assets/component-klaviyo-add-to-cart.js) — not here.
            kbPushDataLayerBundle(form, items);
          }
        },
      }
    );
  }
}

if (!customElements.get('jr-kit-builder')) {
  customElements.define('jr-kit-builder', JrKitBuilder);
}
